// src/pages/stylist/PromotionsPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useUI } from '../../context/UIContext';
import { formatCurrency } from '../../utils/formatters';
import promotionService, { StylistPromotion, CreatePromotionData } from '../../services/promotionService';
import {
  PROMOTION_SERVICE_CATEGORIES,
  SERVICE_CATEGORY_LABELS,
  SERVICE_CATEGORY_EMOJIS,
  type ServiceCategory,
} from '../../constants/serviceCategories';

// ─── Types ────────────────────────────────────────────────────────────────────

interface FormState {
  title: string;
  description: string;
  discountType: 'percentage' | 'fixed';
  discountValue: string;
  minBookingAmount: string;
  maxDiscountAmount: string;
  serviceCategory: ServiceCategory;
  startDate: string;
  endDate: string;
  isActive: boolean;
}

const EMPTY_FORM: FormState = {
  title: '',
  description: '',
  discountType: 'percentage',
  discountValue: '',
  minBookingAmount: '',
  maxDiscountAmount: '',
  serviceCategory: 'all',
  startDate: '',
  endDate: '',
  isActive: true,
};

// Pre-built templates
const TEMPLATES: Array<{ label: string; form: Partial<FormState> }> = [
  {
    label: '🔥 Flash 20% Off',
    form: { title: 'Flash Sale – 20% Off', discountType: 'percentage', discountValue: '20', serviceCategory: 'all' },
  },
  {
    label: '💅 Nails $10 Off',
    form: { title: '$10 Off Nails', discountType: 'fixed', discountValue: '10', serviceCategory: 'nails' },
  },
  {
    label: '✂️ Hair 15% Off',
    form: { title: 'Hair Special – 15% Off', discountType: 'percentage', discountValue: '15', serviceCategory: 'hair' },
  },
  {
    label: '🌸 Braiding 25% Off',
    form: { title: 'Braiding Promo – 25% Off', discountType: 'percentage', discountValue: '25', serviceCategory: 'braiding' },
  },
  {
    label: '💈 Barber $5 Off',
    form: { title: 'Barber Deal – $5 Off', discountType: 'fixed', discountValue: '5', serviceCategory: 'barber' },
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDiscount = (p: StylistPromotion): string =>
  p.discountType === 'percentage' ? `${p.discountValue}% off` : `${formatCurrency(p.discountValue)} off`;

const isExpired = (p: StylistPromotion): boolean =>
  !!p.endDate && new Date(p.endDate) < new Date();

// ─── Component ────────────────────────────────────────────────────────────────

const PromotionsPage: React.FC = () => {
  const { showSnackbar } = useUI();

  const [promotions, setPromotions] = useState<StylistPromotion[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  // Delete confirmation
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const fetchPromotions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await promotionService.getMyPromotions();
      setPromotions(data);
    } catch (err: any) {
      showSnackbar(err?.response?.data?.message || 'Failed to load promotions', 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    fetchPromotions();
  }, [fetchPromotions]);

  // ── Form helpers ────────────────────────────────────────────────────────────

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (promo: StylistPromotion) => {
    setEditingId(promo._id);
    setForm({
      title: promo.title,
      description: promo.description || '',
      discountType: promo.discountType,
      discountValue: String(promo.discountValue),
      minBookingAmount: promo.minBookingAmount ? String(promo.minBookingAmount) : '',
      maxDiscountAmount: promo.maxDiscountAmount ? String(promo.maxDiscountAmount) : '',
      serviceCategory: (promo.serviceCategory as ServiceCategory) || 'all',
      startDate: promo.startDate ? promo.startDate.slice(0, 10) : '',
      endDate: promo.endDate ? promo.endDate.slice(0, 10) : '',
      isActive: promo.isActive,
    });
    setFormError(null);
    setModalOpen(true);
  };

  const applyTemplate = (t: typeof TEMPLATES[0]) => {
    setForm(prev => ({ ...prev, ...t.form }));
  };

  const handleFieldChange = (field: keyof FormState, value: string | boolean) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = (): string | null => {
    if (!form.title.trim()) return 'Title is required.';
    const val = parseFloat(form.discountValue);
    if (isNaN(val) || val <= 0) return 'Discount value must be a positive number.';
    if (form.discountType === 'percentage' && val > 100) return 'Percentage cannot exceed 100.';
    if (!form.startDate) return 'Start date is required.';
    if (!form.endDate) return 'End date is required.';
    const today = new Date().toISOString().slice(0, 10);
    if (!editingId && form.endDate < today) {
      return 'End date cannot be in the past.';
    }
    if (form.endDate <= form.startDate) {
      return 'End date must be after the start date.';
    }
    return null;
  };

  const handleSave = async () => {
    const err = validateForm();
    if (err) { setFormError(err); return; }

    const normalizedTitle = form.title.trim().toLowerCase().replace(/\s+/g, ' ');
    const duplicatePromotion = promotions.some((promo) => {
      if (editingId && promo._id === editingId) return false;
      const promoTitle = (promo.title || '').trim().toLowerCase().replace(/\s+/g, ' ');
      const promoStart = promo.startDate ? promo.startDate.slice(0, 10) : '';
      const promoEnd = promo.endDate ? promo.endDate.slice(0, 10) : '';
      return promoTitle === normalizedTitle && promoStart === form.startDate && promoEnd === form.endDate;
    });
    if (duplicatePromotion) {
      setFormError('A promotion with the same title and date window already exists.');
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      const payload: CreatePromotionData = {
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        discountType: form.discountType,
        discountValue: parseFloat(form.discountValue),
        minBookingAmount: form.minBookingAmount ? parseFloat(form.minBookingAmount) : undefined,
        maxDiscountAmount: form.maxDiscountAmount ? parseFloat(form.maxDiscountAmount) : undefined,
        serviceCategory: form.serviceCategory,
        startDate: form.startDate,
        endDate: form.endDate,
        isActive: form.isActive,
      };

      if (editingId) {
        await promotionService.updatePromotion(editingId, payload);
        showSnackbar('Promotion updated!', 'success');
      } else {
        await promotionService.createPromotion(payload);
        showSnackbar('Promotion created!', 'success');
      }
      setModalOpen(false);
      fetchPromotions();
    } catch (err: any) {
      setFormError(err?.response?.data?.message || 'Failed to save promotion.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (promo: StylistPromotion) => {
    try {
      await promotionService.togglePromotion(promo._id, !promo.isActive);
      setPromotions(prev =>
        prev.map(p => p._id === promo._id ? { ...p, isActive: !p.isActive } : p)
      );
    } catch {
      showSnackbar('Failed to update status', 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await promotionService.deletePromotion(deleteId);
      setPromotions(prev => prev.filter(p => p._id !== deleteId));
      showSnackbar('Promotion deleted', 'success');
    } catch {
      showSnackbar('Failed to delete promotion', 'error');
    } finally {
      setDeleteId(null);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '24px', maxWidth: 900, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>🎁 My Promotions</h1>
          <p style={{ margin: '4px 0 0', color: '#666', fontSize: 14 }}>
            Create and manage discounts that clients see when booking your services.
          </p>
        </div>
        <button
          onClick={openCreate}
          style={{
            background: 'linear-gradient(135deg, #C9A84C, #A8872A)',
            color: '#fff',
            border: 'none',
            borderRadius: 10,
            padding: '10px 20px',
            fontWeight: 600,
            cursor: 'pointer',
            fontSize: 14,
          }}
        >
          + New Promotion
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>Loading promotions…</div>
      ) : promotions.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: 60, background: '#f9f9f9',
          borderRadius: 16, border: '2px dashed #e0e0e0',
        }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🎁</div>
          <h3 style={{ margin: '0 0 8px', color: '#333' }}>No promotions yet</h3>
          <p style={{ color: '#999', marginBottom: 16 }}>Create your first promotion to attract more clients.</p>
          <button
            onClick={openCreate}
            style={{
              background: 'linear-gradient(135deg, #C9A84C, #A8872A)',
              color: '#fff', border: 'none', borderRadius: 10,
              padding: '10px 20px', fontWeight: 600, cursor: 'pointer',
            }}
          >
            Create Promotion
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 16 }}>
          {promotions.map(promo => {
            const expired = isExpired(promo);
            return (
              <div
                key={promo._id}
                style={{
                  background: '#fff',
                  borderRadius: 14,
                  border: `1.5px solid ${promo.isActive && !expired ? '#E8E0CC' : '#e0e0e0'}`,
                  padding: '16px 20px',
                  display: 'flex',
                  gap: 16,
                  alignItems: 'flex-start',
                  opacity: expired ? 0.6 : 1,
                }}
              >
                {/* Category emoji */}
                <div style={{
                  width: 48, height: 48, borderRadius: 12,
                  background: 'linear-gradient(135deg, #FAF8F3, #FAF8F3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 22, flexShrink: 0,
                }}>
                  {SERVICE_CATEGORY_EMOJIS[promo.serviceCategory as ServiceCategory] || '🎁'}
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontWeight: 700, fontSize: 16 }}>{promo.title}</span>
                    <span style={{
                      background: 'linear-gradient(135deg, #C9A84C, #A8872A)',
                      color: '#fff', borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 600,
                    }}>
                      {formatDiscount(promo)}
                    </span>
                    <span style={{
                      background: '#FAF8F3', color: '#C9A84C',
                      borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 500,
                    }}>
                      {SERVICE_CATEGORY_LABELS[promo.serviceCategory as ServiceCategory] || promo.serviceCategory}
                    </span>
                    {expired && (
                      <span style={{ background: '#FCEAE8', color: '#C0392B', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                        Expired
                      </span>
                    )}
                  </div>
                  {promo.description && (
                    <p style={{ margin: '4px 0 0', color: '#666', fontSize: 13 }}>{promo.description}</p>
                  )}
                  <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 12, color: '#888', flexWrap: 'wrap' }}>
                    {(promo.minBookingAmount ?? 0) > 0 && (
                      <span>Min: {formatCurrency(promo.minBookingAmount ?? 0)}</span>
                    )}
                    {promo.endDate && (
                      <span>Expires: {new Date(promo.endDate).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>

                {/* Controls */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end', flexShrink: 0 }}>
                  {/* Toggle */}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                    <div
                      onClick={() => handleToggleActive(promo)}
                      style={{
                        width: 40, height: 22, borderRadius: 11,
                        background: promo.isActive ? '#C9A84C' : '#ccc',
                        position: 'relative', cursor: 'pointer', transition: 'background 0.2s',
                      }}
                    >
                      <div style={{
                        width: 18, height: 18, borderRadius: '50%', background: '#fff',
                        position: 'absolute', top: 2,
                        left: promo.isActive ? 20 : 2,
                        transition: 'left 0.2s',
                      }} />
                    </div>
                    <span style={{ color: promo.isActive ? '#C9A84C' : '#999' }}>
                      {promo.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </label>

                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => openEdit(promo)}
                      style={{
                        background: '#FAF8F3', color: '#C9A84C', border: 'none',
                        borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setDeleteId(promo._id)}
                      style={{
                        background: '#FCEAE8', color: '#C0392B', border: 'none',
                        borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Create / Edit Modal ──────────────────────────────────────────────── */}
      {modalOpen && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: 16,
          }}
          onClick={e => { if (e.target === e.currentTarget) setModalOpen(false); }}
        >
          <div style={{
            background: '#fff', borderRadius: 20, width: '100%', maxWidth: 560,
            maxHeight: '90vh', overflow: 'auto', padding: 24,
          }}>
            <h2 style={{ margin: '0 0 16px', fontSize: 20, fontWeight: 700 }}>
              {editingId ? 'Edit Promotion' : 'New Promotion'}
            </h2>

            {/* Templates (only on create) */}
            {!editingId && (
              <div style={{ marginBottom: 20 }}>
                <p style={{ margin: '0 0 8px', fontWeight: 600, fontSize: 13, color: '#555' }}>Quick Templates</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {TEMPLATES.map(t => (
                    <button
                      key={t.label}
                      onClick={() => applyTemplate(t)}
                      style={{
                        background: '#FAF8F3', color: '#C9A84C', border: 'none',
                        borderRadius: 20, padding: '6px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 600,
                      }}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {formError && (
              <div style={{ background: '#FCEAE8', color: '#C0392B', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
                {formError}
              </div>
            )}

            <div style={{ display: 'grid', gap: 14 }}>
              {/* Title */}
              <div>
                <label style={labelStyle}>Title *</label>
                <input
                  style={inputStyle}
                  value={form.title}
                  onChange={e => handleFieldChange('title', e.target.value)}
                  placeholder="e.g. Summer Sale – 20% Off"
                />
              </div>

              {/* Description */}
              <div>
                <label style={labelStyle}>Description (optional)</label>
                <textarea
                  style={{ ...inputStyle, height: 68, resize: 'vertical' }}
                  value={form.description}
                  onChange={e => handleFieldChange('description', e.target.value)}
                  placeholder="Brief description of the promotion…"
                />
              </div>

              {/* Discount Type + Value */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Discount Type *</label>
                  <select
                    style={inputStyle}
                    value={form.discountType}
                    onChange={e => handleFieldChange('discountType', e.target.value)}
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed ($)</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>
                    Discount Value * {form.discountType === 'percentage' ? '(%)' : '($)'}
                  </label>
                  <input
                    style={inputStyle}
                    type="number"
                    min={1}
                    max={form.discountType === 'percentage' ? 100 : undefined}
                    value={form.discountValue}
                    onChange={e => handleFieldChange('discountValue', e.target.value)}
                    placeholder={form.discountType === 'percentage' ? '20' : '10'}
                  />
                </div>
              </div>

              {/* Service Category */}
              <div>
                <label style={labelStyle}>Service Category</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                  {PROMOTION_SERVICE_CATEGORIES.map(cat => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => handleFieldChange('serviceCategory', cat)}
                      style={{
                        border: form.serviceCategory === cat ? '2px solid #C9A84C' : '1.5px solid #e0e0e0',
                        background: form.serviceCategory === cat ? '#FAF8F3' : '#fff',
                        color: form.serviceCategory === cat ? '#C9A84C' : '#555',
                        borderRadius: 20, padding: '5px 12px', fontSize: 12, cursor: 'pointer', fontWeight: 600,
                      }}
                    >
                      {SERVICE_CATEGORY_EMOJIS[cat]} {SERVICE_CATEGORY_LABELS[cat]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Optional fields */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Min Booking Amount ($)</label>
                  <input
                    style={inputStyle}
                    type="number"
                    min={0}
                    value={form.minBookingAmount}
                    onChange={e => handleFieldChange('minBookingAmount', e.target.value)}
                    placeholder="0 (no minimum)"
                  />
                </div>
                {form.discountType === 'percentage' && (
                  <div>
                    <label style={labelStyle}>Max Discount Cap ($)</label>
                    <input
                      style={inputStyle}
                      type="number"
                      min={0}
                      value={form.maxDiscountAmount}
                      onChange={e => handleFieldChange('maxDiscountAmount', e.target.value)}
                      placeholder="No cap"
                    />
                  </div>
                )}
              </div>

              {/* Dates */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Start Date *</label>
                  <input
                    style={inputStyle}
                    type="date"
                    value={form.startDate}
                    onChange={e => handleFieldChange('startDate', e.target.value)}
                  />
                </div>
                <div>
                  <label style={labelStyle}>End Date *</label>
                  <input
                    style={inputStyle}
                    type="date"
                    value={form.endDate}
                    onChange={e => handleFieldChange('endDate', e.target.value)}
                  />
                </div>
              </div>

              {/* Active toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <label style={{ fontWeight: 600, fontSize: 13, color: '#555' }}>Active</label>
                <div
                  onClick={() => handleFieldChange('isActive', !form.isActive)}
                  style={{
                    width: 44, height: 24, borderRadius: 12,
                    background: form.isActive ? '#C9A84C' : '#ccc',
                    position: 'relative', cursor: 'pointer',
                  }}
                >
                  <div style={{
                    width: 20, height: 20, borderRadius: '50%', background: '#fff',
                    position: 'absolute', top: 2, left: form.isActive ? 22 : 2, transition: 'left 0.2s',
                  }} />
                </div>
                <span style={{ fontSize: 13, color: form.isActive ? '#C9A84C' : '#999' }}>
                  {form.isActive ? 'Clients can see & apply this promotion' : 'Hidden from clients'}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
              <button
                onClick={() => setModalOpen(false)}
                style={{ background: '#f0f0f0', border: 'none', borderRadius: 10, padding: '10px 20px', cursor: 'pointer', fontWeight: 600 }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  background: saving ? '#ccc' : 'linear-gradient(135deg, #C9A84C, #A8872A)',
                  color: '#fff', border: 'none', borderRadius: 10,
                  padding: '10px 24px', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600,
                }}
              >
                {saving ? 'Saving…' : editingId ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation ──────────────────────────────────────────────── */}
      {deleteId && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001, padding: 16,
          }}
        >
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, maxWidth: 380, width: '100%' }}>
            <h3 style={{ margin: '0 0 10px', fontSize: 18 }}>Delete Promotion?</h3>
            <p style={{ color: '#666', marginBottom: 24 }}>
              This cannot be undone. Clients will no longer see this promotion.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button
                onClick={() => setDeleteId(null)}
                style={{ background: '#f0f0f0', border: 'none', borderRadius: 10, padding: '10px 20px', cursor: 'pointer', fontWeight: 600 }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                style={{ background: '#C0392B', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 20px', cursor: 'pointer', fontWeight: 600 }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Micro styles ─────────────────────────────────────────────────────────────

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontWeight: 600,
  fontSize: 13,
  color: '#555',
  marginBottom: 5,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  border: '1.5px solid #e0e0e0',
  borderRadius: 10,
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};

export default PromotionsPage;
